import { z } from 'zod';
import { AdCreativeType, FBCallToAction } from './AdCreative';

// Shared contract for the Creative Import feature (bulk ZIP import of draft
// creatives). Source of truth for the manifest schema, validation-report DTO,
// job/row statuses, limits, and the supported-CTA set.

export const CREATIVE_IMPORT_MANIFEST_VERSION = 1;

/** Manifest filename expected at the normalized archive root. */
export const CREATIVE_IMPORT_MANIFEST_FILENAME = 'creatives.json';

export const CREATIVE_IMPORT_LIMITS = {
  /** Hard cap on manifest rows per import (expected batches are ≤50). */
  maxRows: 100,
  /** Matches the persisted creative/effective-name varchar length. */
  creativeNameMaxLength: 255,
  /**
   * Hard cap on an edited headline: the created creative stores it in a
   * varchar(255) column (`link_headline`), so a longer value would fail at
   * creation. Blocks at Review, unlike the recommended length below.
   */
  headlineMaxLength: 255,
  /**
   * Hard cap on the fully composed destination URL (after url_parameters are
   * merged): the created creative stores it in a varchar(255) column
   * (`link_website_url`). Blocks at Review.
   */
  destinationUrlMaxLength: 255,
  /** Meta's recommended primary-text length — warn-only (the sink is unlimited `text`). */
  primaryTextRecommendedMax: 125,
  /** Meta's recommended headline length — warn-only (distinct from the 255 hard cap). */
  headlineRecommendedMax: 40,
  /** Meta's recommended description length — warn-only (the sink is unlimited `text`). */
  descriptionRecommendedMax: 30,
  /** Per-image size that triggers a warning. */
  imageWarnBytes: 8 * 1024 * 1024,
  /** Per-image hard limit (Facebook's own image ceiling). */
  imageMaxBytes: 30 * 1024 * 1024,
  /** A social video ad is 5–30 MB; past this it's likely an unexported master. */
  videoWarnBytes: 50 * 1024 * 1024,
  /** Per-video hard wall. */
  videoMaxBytes: 200 * 1024 * 1024,
  /**
   * Hard cap on media files across the whole manifest. `maxRows` counts
   * *entries*, and one carousel entry is up to `carouselSlidesMax` files, so
   * entries alone no longer bound the work.
   */
  maxMediaTotal: 200,
  /**
   * Sum of the media the manifest actually references. A file count doesn't
   * bound the worker's memory (it buffers the archive plus inflated entries);
   * this does. It is a memory bound, not a disk bound — do not raise it while
   * the worker still buffers whole archives instead of streaming them.
   */
  maxReferencedBytes: 300 * 1024 * 1024,
  /**
   * Carousel slide bounds. Deliberately stricter than our own wizard and
   * Facebook (both allow 10) because 2–5 is what the published packaging
   * prompt promises. Raising it is this constant plus a prompt edit.
   */
  carouselSlidesMin: 2,
  carouselSlidesMax: 5,
  /** Facebook's `asset_feed_spec` media limit — reject before Facebook does. */
  dynamicMaxMedia: 10,
  /** Facebook's `asset_feed_spec` per-pool text-variant limit. */
  dynamicMaxTextVariants: 5,
  /** Recommended `justification` length — warn-only; the sink is unlimited `text`. */
  justificationRecommendedMax: 500,
  /** Hard limit on total extracted archive size. */
  totalExtractedMaxBytes: 500 * 1024 * 1024,
  /** Extracted:compressed ratio above which the archive is rejected (zip-bomb guard). */
  maxCompressionRatio: 100,
  /** Hard cap on ZIP entries walked — stops entry-flood archives (millions of zero-byte files) before byte limits can. */
  maxArchiveEntries: 2000,
  /** Max rows processed concurrently by the import worker (per job). */
  workerConcurrency: 3,
  /** How long a staged archive is guaranteed to survive between validate and confirm. */
  stagingTtlHours: 24,
  /** "The last handful" shown in the Recent imports menu — server-enforced. */
  recentImportsMax: 6,
} as const;

/**
 * Oldest `createdAt` still confirmable. A staged archive is expired iff
 * `createdAt < cutoff`. This is the primitive both `confirm` and the recent-
 * imports list must agree on: confirm rejects past it, the list hides past it,
 * and Review counts down to it.
 */
export function creativeImportStagingCutoff(now: number = Date.now()): Date {
  return new Date(
    now - CREATIVE_IMPORT_LIMITS.stagingTtlHours * 60 * 60 * 1000,
  );
}

/** The absolute instant a staged archive stops being confirmable. */
export function creativeImportStagingExpiresAt(createdAt: Date | string): Date {
  return new Date(
    new Date(createdAt).getTime() +
      CREATIVE_IMPORT_LIMITS.stagingTtlHours * 60 * 60 * 1000,
  );
}

/** Defined in terms of the cutoff so the SQL bound and the boolean can't disagree. */
export function creativeImportStagingExpired(
  createdAt: Date | string,
  now: number = Date.now(),
): boolean {
  return (
    new Date(createdAt).getTime() < creativeImportStagingCutoff(now).getTime()
  );
}

export const CREATIVE_IMPORT_ALLOWED_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
] as const;

/**
 * GIF is here rather than with the images because Facebook ingests it through
 * the *video* pipeline (`/advideos`), so the importer must too. It still
 * renders as an image in a browser — the Review preview branches on extension,
 * not on the format we send Facebook.
 */
export const CREATIVE_IMPORT_ALLOWED_VIDEO_EXTENSIONS = [
  'mp4',
  'mov',
  'gif',
] as const;

/** How a referenced media file is uploaded: Facebook's image or video endpoint. */
export type CreativeImportMediaKind = 'image' | 'video';

export function extensionOf(path: string): string {
  return path.split('.').pop()?.toLowerCase() ?? '';
}

export function isAllowedImagePath(path: string): boolean {
  return (CREATIVE_IMPORT_ALLOWED_IMAGE_EXTENSIONS as readonly string[]).includes(
    extensionOf(path),
  );
}

export function isAllowedVideoPath(path: string): boolean {
  return (CREATIVE_IMPORT_ALLOWED_VIDEO_EXTENSIONS as readonly string[]).includes(
    extensionOf(path),
  );
}

/** The upload pipeline a path belongs to; undefined for an unsupported extension. */
export function mediaKindForPath(
  path: string,
): CreativeImportMediaKind | undefined {
  if (isAllowedImagePath(path)) return 'image';
  if (isAllowedVideoPath(path)) return 'video';
  return undefined;
}

// —— Supported CTAs ————————————————————————————————————————————————————————
// Server-owned CTA support list — the single source for both import
// validation and the wizard dropdown. Notably BOOK_NOW is unsupported — use
// REQUEST_TIME for appointments/bookings.

export const UNSUPPORTED_CALL_TO_ACTION_FIELDS: Partial<
  Array<FBCallToAction>
> = [
  FBCallToAction.BUY,
  FBCallToAction.CALL,
  FBCallToAction.CALL_ME,
  // Allowed only on single image / video, and only with a phone number — see
  // CALLS_CALL_TO_ACTION. Kept here so import, copy edits and the generic dropdowns never offer it.
  FBCallToAction.CALL_NOW,
  FBCallToAction.CONTACT,
  FBCallToAction.FIND_A_GROUP,
  FBCallToAction.FOLLOW_NEWS_STORYLINE,
  FBCallToAction.FOLLOW_PAGE,
  FBCallToAction.GET_DIRECTIONS,
  FBCallToAction.LIKE_PAGE,
  FBCallToAction.MESSAGE_PAGE,
  FBCallToAction.RECORD_NOW,
  FBCallToAction.REFER_FRIENDS,
  FBCallToAction.SAY_THANKS,
  FBCallToAction.SEND_A_GIFT,
  FBCallToAction.SHARE,
  FBCallToAction.SOTTO_SUBSCRIBE,
  FBCallToAction.START_ORDER,
  FBCallToAction.VIDEO_ANNOTATION,
  FBCallToAction.VISIT_PAGES_FEED,
  FBCallToAction.WOODHENGE_SUPPORT,
  FBCallToAction.BOOK_NOW,
];

export const SUPPORTED_CALL_TO_ACTIONS: FBCallToAction[] = Object.values(
  FBCallToAction,
).filter((cta) => !UNSUPPORTED_CALL_TO_ACTION_FIELDS.includes(cta));

export const CREATIVE_IMPORT_DEFAULT_CALL_TO_ACTION = FBCallToAction.LEARN_MORE;

/**
 * The button a calls ad carries. The only CTA outside SUPPORTED_CALL_TO_ACTIONS that the builder
 * offers, and only on single image / video creatives with a phone number.
 */
export const CALLS_CALL_TO_ACTION = FBCallToAction.CALL_NOW;

export function isSupportedCallToAction(
  value: string,
): value is FBCallToAction {
  return SUPPORTED_CALL_TO_ACTIONS.includes(value as FBCallToAction);
}

/**
 * True when `value` parses as an http(s) URL. Shared across the validator
 * (warn on a bad manifest URL), the worker (fall back to the business website),
 * and the Review UI (inline validation of an edited destination URL). The
 * `URL` constructor is available in both node and the browser.
 */
export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

const VALID_URL_PARAMETER = /^[^=&\s]+=[^&\s]*$/;

/**
 * Merges manifest `url_parameters` onto a base destination URL. Existing query
 * keys on the base win on conflict; fragments stay on the base. Anything
 * unparseable is dropped (validation already downgraded it to a warning).
 * Shared by the worker (compose the final URL), the confirm service and the
 * Review UI (length-check the composed URL against the 255 sink). Uses only
 * `URL`/`URLSearchParams`, available in both node and the browser.
 */
export function mergeUrlParameters(
  baseUrl: string,
  urlParameters?: string,
): string {
  if (!urlParameters) return baseUrl;
  const stripped = urlParameters.replace(/^[?&]/, '');
  if (!stripped) return baseUrl;
  if (
    !stripped
      .split('&')
      .every((parameter) => VALID_URL_PARAMETER.test(parameter))
  )
    return baseUrl;
  try {
    const url = new URL(baseUrl);
    const extra = new URLSearchParams(stripped);
    extra.forEach((value, key) => {
      if (!url.searchParams.has(key)) url.searchParams.append(key, value);
    });
    return url.toString();
  } catch {
    return baseUrl;
  }
}

// —— Manifest schema ————————————————————————————————————————————————————————
// The manifest is a JSON file: { manifest_version: 1, creatives: [...] }.
// Within a creative entry only image_path is required (plan v2.3 scope-down):
// the manifest is a selector; copy is optional and drafts import intentionally
// incomplete. The validator trims values and drops empty-after-trim ones
// before the row schema runs.

/**
 * A path that stays inside the archive. Exported so error classification can ask
 * which refinement failed — "unsafe path" and "wrong file type" are different
 * problems with different fixes, and inferring one from the other gets it wrong.
 */
export const isSafeRelativePath = (val: string) =>
  !val.startsWith('/') &&
  !/^[a-zA-Z]:[\\/]/.test(val) &&
  !val.split(/[\\/]/).some((segment) => segment === '..');

const MEDIA_PATH_UNSAFE =
  'media_path must be a relative path inside the archive (no absolute paths or "..")';
const IMAGE_TYPE_MESSAGE = 'image must be a .jpg, .jpeg, or .png file';
const VIDEO_TYPE_MESSAGE = 'video must be a .mp4, .mov, or .gif file';
const MEDIA_TYPE_MESSAGE =
  'media must be a .jpg, .jpeg, .png, .mp4, .mov, or .gif file';

function mediaPath(
  required: string,
  typeMessage: string,
  allowed: (p: string) => boolean,
) {
  return z
    .string({ required_error: required, invalid_type_error: required })
    .min(1, { message: required })
    .refine(isSafeRelativePath, { message: MEDIA_PATH_UNSAFE })
    .refine(allowed, { message: typeMessage });
}

const imagePathSchema = mediaPath(
  'media_path is required — each row must select a file',
  IMAGE_TYPE_MESSAGE,
  isAllowedImagePath,
);
const videoPathSchema = mediaPath(
  'media_path is required — each row must select a file',
  VIDEO_TYPE_MESSAGE,
  isAllowedVideoPath,
);
const anyMediaPathSchema = mediaPath(
  'media_path is required — each slide/asset must select a file',
  MEDIA_TYPE_MESSAGE,
  (path) => mediaKindForPath(path) !== undefined,
);

// Fields every format carries. The copy fields are optional pass-through: the
// manifest is a selector and drafts import intentionally incomplete.
const sharedRowFields = {
  /** Defaults to the media filename without extension; editable at Review. */
  creative_name: z
    .string()
    .max(CREATIVE_IMPORT_LIMITS.creativeNameMaxLength, {
      message: `creative_name must be ${CREATIVE_IMPORT_LIMITS.creativeNameMaxLength} characters or fewer`,
    })
    .optional(),
  primary_text: z.string().optional(),
  /** Persists as the creative entity's linkHeadline. */
  headline: z.string().optional(),
  /** DTO name is `description`; persists as the entity's linkDescription (what the completeness gate checks). */
  description: z.string().optional(),
  /** Warn-and-fallback to LEARN_MORE when unsupported (never a hard error). */
  call_to_action: z.string().optional(),
  /** Warn-and-fallback to the business website when not http(s). */
  destination_url: z.string().optional(),
  /** `&`-joined query string merged onto the effective URL; malformed is warn-only. */
  url_parameters: z.string().optional(),
  graphic_text: z.string().optional(),
  /**
   * S7d: why this ad exists, written by the client's AI at build time and read
   * later by the sprint packet's strategy section. Warn-only on length; never
   * blocks. The prompt may only carry over reasoning that already exists in the
   * chat — it must never invent one.
   */
  justification: z.string().optional(),
};

/** One carousel card: a single media file plus its own headline/description/link. */
export const creativeImportCarouselSlideSchema = z.object({
  media_path: anyMediaPathSchema,
  headline: z.string().optional(),
  description: z.string().optional(),
  destination_url: z.string().optional(),
});

export type CreativeImportCarouselSlide = z.infer<
  typeof creativeImportCarouselSlideSchema
>;

export const creativeImportSingleImageRowSchema = z.object({
  format: z.literal(AdCreativeType.single_image),
  media_path: imagePathSchema,
  ...sharedRowFields,
});

export const creativeImportSingleVideoRowSchema = z.object({
  format: z.literal(AdCreativeType.single_video),
  media_path: videoPathSchema,
  ...sharedRowFields,
});

export const creativeImportCarouselRowSchema = z.object({
  format: z.literal(AdCreativeType.carousel),
  slides: z
    .array(creativeImportCarouselSlideSchema)
    .min(CREATIVE_IMPORT_LIMITS.carouselSlidesMin, {
      message: `a carousel needs at least ${CREATIVE_IMPORT_LIMITS.carouselSlidesMin} slides`,
    })
    .max(CREATIVE_IMPORT_LIMITS.carouselSlidesMax, {
      message: `a carousel can have at most ${CREATIVE_IMPORT_LIMITS.carouselSlidesMax} slides`,
    }),
  ...sharedRowFields,
});

export const creativeImportDynamicRowSchema = z.object({
  format: z.literal(AdCreativeType.dynamic),
  media_paths: z
    .array(anyMediaPathSchema)
    .min(1, { message: 'a dynamic ad needs at least one media file' })
    .max(CREATIVE_IMPORT_LIMITS.dynamicMaxMedia, {
      message: `a dynamic ad can have at most ${CREATIVE_IMPORT_LIMITS.dynamicMaxMedia} media files`,
    }),
  // Copy pools. Ordered text with no per-item state, so they stay inline rather
  // than getting a child table (unlike media, which has real lifecycle state).
  primary_texts: textPool(),
  headlines: textPool(),
  descriptions: textPool(),
  call_to_actions: textPool(),
  ...sharedRowFields,
});

function textPool() {
  return z
    .array(z.string())
    .max(CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants, {
      message: `each copy pool can have at most ${CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants} variants`,
    })
    .optional();
}

/** The manifest row, discriminated on `format`; legacy rows default to single image before parsing. */
export const creativeImportRowSchema = z.discriminatedUnion('format', [
  creativeImportSingleImageRowSchema,
  creativeImportSingleVideoRowSchema,
  creativeImportCarouselRowSchema,
  creativeImportDynamicRowSchema,
]);

export type CreativeImportRow = z.infer<typeof creativeImportRowSchema>;

/** One media file a row references, in the order the format defines. */
export interface CreativeImportMediaRef {
  /** 0-based position: the carousel slide order, or the dynamic asset order. */
  ordinal: number;
  path: string;
  kind: CreativeImportMediaKind | undefined;
}

/**
 * THE ordered traversal of a row's media. Validation, orphan counting,
 * media-child creation, browser thumbnail extraction, and the worker all read
 * media through this — they must not each grow their own idea of where it
 * lives. Tolerates the pre-S7 `image_path` spelling for rows persisted before
 * the dialect switch.
 */
export function mediaRefs(row: CreativeImportRow): CreativeImportMediaRef[] {
  const ref = (path: string, ordinal: number): CreativeImportMediaRef => ({
    ordinal,
    path,
    kind: mediaKindForPath(path),
  });
  switch (row.format) {
    case AdCreativeType.carousel:
      return (row.slides ?? [])
        .map((slide, index) =>
          typeof slide?.media_path === 'string'
            ? ref(slide.media_path, index)
            : null,
        )
        .filter((entry): entry is CreativeImportMediaRef => entry !== null);
    case AdCreativeType.dynamic:
      return (row.media_paths ?? [])
        .map((path, index) =>
          typeof path === 'string' ? ref(path, index) : null,
        )
        .filter((entry): entry is CreativeImportMediaRef => entry !== null);
    default: {
      const legacy = ((row as unknown) as { image_path?: unknown }).image_path;
      const path =
        typeof row.media_path === 'string'
          ? row.media_path
          : typeof legacy === 'string'
          ? legacy
          : undefined;
      return path ? [ref(path, 0)] : [];
    }
  }
}

// Top-level manifest shape (structural check only — creative entries are
// validated one by one with creativeImportRowSchema so the report stays
// per-row). manifest_version lives at the file level; a wrong version rejects
// the whole archive. The string form '1' is accepted for AI-tool leniency.
export const creativeImportManifestSchema = z.object({
  manifest_version: z.union(
    [
      z.literal(CREATIVE_IMPORT_MANIFEST_VERSION),
      z.literal(String(CREATIVE_IMPORT_MANIFEST_VERSION)),
    ],
    {
      errorMap: () => ({
        message: `manifest_version is required at the top level and must be ${CREATIVE_IMPORT_MANIFEST_VERSION}`,
      }),
    },
  ),
  creatives: z.array(z.record(z.unknown()), {
    errorMap: () => ({
      message: 'creatives is required and must be an array of creative entries',
    }),
  }),
});

export type CreativeImportManifest = z.infer<
  typeof creativeImportManifestSchema
>;

/** Manifest fields the importer recognizes but deliberately ignores (with a warning). */
export const CREATIVE_IMPORT_IGNORED_FIELDS = [
  'facebook_page_id',
  'instagram_account_id',
  'facebook_account_id',
  'status',
] as const;

// —— Job / row statuses (plan §6, v1 sets) —————————————————————————————————

export enum CreativeImportJobStatus {
  AwaitingConfirmation = 'awaiting_confirmation',
  Queued = 'queued',
  Processing = 'processing',
  Completed = 'completed',
  CompletedWithErrors = 'completed_with_errors',
  Failed = 'failed',
  /** Terminal: the worker process died and Bull's stall recovery is exhausted; unfinished rows are recovered by re-import. */
  Interrupted = 'interrupted',
}

export enum CreativeImportRowStatus {
  NotSelected = 'not_selected',
  Pending = 'pending',
  Processing = 'processing',
  Done = 'done',
  Error = 'error',
}

/** Last step the worker completed for a row — lets a future retry resume instead of restarting (plan §6/§12). */
export enum CreativeImportRowStep {
  ImageUploaded = 'image_uploaded',
  CreativeCreated = 'creative_created',
  SprintAttached = 'sprint_attached',
}

// —— Validation report (phase-1 response, renders the Review table) ————————

export enum CreativeImportIssueCode {
  // row-level hard errors (row cannot be selected)
  ImageMissing = 'image_missing',
  ImageNotFound = 'image_not_found',
  ImagePathUnsafe = 'image_path_unsafe',
  ImagePathAmbiguous = 'image_path_ambiguous',
  ImageTypeUnsupported = 'image_type_unsupported',
  ImageTooLarge = 'image_too_large',
  VideoTypeUnsupported = 'video_type_unsupported',
  VideoTooLarge = 'video_too_large',
  /** The extension belongs to neither pipeline (carousel slide / dynamic asset). */
  MediaTypeUnsupported = 'media_type_unsupported',
  FormatUnsupported = 'format_unsupported',
  CreativeNameTooLong = 'creative_name_too_long',
  /** Slide count outside carouselSlidesMin..Max. */
  CarouselSlidesInvalid = 'carousel_slides_invalid',
  /** Asset count outside 1..dynamicMaxMedia. */
  DynamicMediaInvalid = 'dynamic_media_invalid',
  /** A copy pool exceeds dynamicMaxTextVariants. */
  DynamicTextVariantsInvalid = 'dynamic_text_variants_invalid',
  // archive-level hard errors (whole upload rejected)
  ManifestVersionUnsupported = 'manifest_version_unsupported',
  ManifestMissing = 'manifest_missing',
  ManifestAmbiguous = 'manifest_ambiguous',
  /** Manifest is not parseable JSON or has the wrong top-level shape. */
  ManifestInvalid = 'manifest_invalid',
  ArchiveTooLarge = 'archive_too_large',
  TooManyRows = 'too_many_rows',
  /** More referenced media files than maxMediaTotal. */
  TooManyMediaFiles = 'too_many_media_files',
  /** Referenced media bytes exceed maxReferencedBytes (bounds worker memory). */
  ReferencedMediaTooLarge = 'referenced_media_too_large',
  // warnings
  DuplicateName = 'duplicate_name',
  CallToActionUnsupported = 'call_to_action_unsupported',
  DestinationUrlInvalid = 'destination_url_invalid',
  UrlParametersInvalid = 'url_parameters_invalid',
  ImageLarge = 'image_large',
  VideoLarge = 'video_large',
  /** S7d: justification past the recommended length — never blocks. */
  JustificationLong = 'justification_long',
  CopyIncomplete = 'copy_incomplete',
  TextMayBeRejected = 'text_may_be_rejected',
  ManifestDetectedByFallback = 'manifest_detected_by_fallback',
  IgnoredFieldsPresent = 'ignored_fields_present',
}

export interface CreativeImportIssue {
  code: CreativeImportIssueCode;
  /** Human-readable, user-facing explanation. */
  message: string;
  /**
   * Manifest field the issue relates to, when it's field-specific — the FULL
   * dotted path, not just the top-level key (`slides.2.media_path`, not
   * `slides`), so Review can point at the cell that's actually wrong.
   */
  field?: string;
}

/** Per-slide / per-asset copy overrides, echoed back so a reopen restores edits. */
export interface CreativeImportSlideState {
  /** 0-based slide order; matches the media child's ordinal. */
  ordinal: number;
  headline?: string;
  description?: string;
  destinationUrl?: string;
}

/** S7c: the dynamic format's editable copy pools. Ordered text, no per-item state. */
export interface CreativeImportCopyPools {
  primaryTexts?: string[];
  headlines?: string[];
  descriptions?: string[];
  callToActions?: string[];
}

export interface CreativeImportRowReport {
  /** 1-indexed manifest data row number. */
  rowNumber: number;
  /** Normalized row values after trim/defaulting (not yet CTA/URL-fallback-applied). */
  row: CreativeImportRow;
  /** The row's ad format. */
  format: AdCreativeType;
  /** Effective creative name after the filename default — seed for the editable name at Review. */
  effectiveName: string;
  /**
   * Seeds for the editable copy cells at Review (EC slice). Text/URL seeds are
   * the normalized manifest value (absent when the manifest omitted it).
   * effectiveCallToAction is fallback-applied (an unsupported manifest CTA
   * seeds as the default) so the dropdown seed is always a valid option.
   */
  effectivePrimaryText?: string;
  effectiveHeadline?: string;
  effectiveDescription?: string;
  effectiveCallToAction: FBCallToAction;
  effectiveDestinationUrl?: string;
  /** S7d: warn-only, never blocks. */
  effectiveJustification?: string;
  /** S7b: per-slide copy seeds, ordered by slide. Carousel rows only. */
  effectiveSlides?: CreativeImportSlideState[];
  /** S7c: copy-pool seeds. Dynamic rows only. */
  effectiveCopyPools?: CreativeImportCopyPools;
  hardErrors: CreativeImportIssue[];
  warnings: CreativeImportIssue[];
  /** Derived: no hard errors. Only selectable rows can be confirmed for import. */
  selectable: boolean;
}

export interface CreativeImportValidationReport {
  /** Persisted import-job ID. Null until S2 adds persistence (field reserved so the endpoint shape is stable). */
  creativeImportID: number | null;
  /** Archive-level hard errors — any entry means the whole upload is rejected. */
  archiveErrors: CreativeImportIssue[];
  archiveWarnings: CreativeImportIssue[];
  rows: CreativeImportRowReport[];
  counts: {
    totalRows: number;
    selectableRows: number;
    warnedRows: number;
    blockedRows: number;
    /** Media in the archive no row references — deliberate exclusions under the selector model; silent info, never a warning. */
    orphanMedia: number;
  };
  /**
   * ISO confirm deadline for the staged archive (slice 12). Optional so the
   * validator — which knows nothing about persistence — needn't set it; the
   * service stamps it once the job exists, on both fresh upload and reopen.
   */
  stagingExpiresAt?: string;
}

// —— Confirm / progress (phase-2 contract; plan §3, §6) —————————————————————

export interface CreativeImportConfirmRequest {
  /**
   * Rows to import, by report rowNumber. `name` carries the Review-edited
   * creative name; the copy fields carry the Review-edited copy overrides (EC
   * slice). An override present as a string (incl. '') wins over the manifest;
   * absent leaves the manifest value in force. An empty string clears the field
   * (except callToAction, which is a constrained dropdown that always has a value).
   */
  rows: Array<{
    rowNumber: number;
    name?: string;
    primaryText?: string;
    headline?: string;
    description?: string;
    callToAction?: FBCallToAction;
    destinationUrl?: string;
    /** S7d: warn-only at Review, so it's never a 400 here. */
    justification?: string;
    /**
     * S7b: per-slide copy for a carousel row. Capped at 255 per field here
     * (including a composed/fallback URL) because the carousel-slide entity is
     * varchar(255) on all three — an overlong value would otherwise fail in the
     * worker, past the point the user can fix it.
     */
    slides?: CreativeImportSlideState[];
    /** S7c: edited copy pools for a dynamic row. */
    copyPools?: CreativeImportCopyPools;
  }>;
  /** Existing sprint to attach imported drafts to (plan §7 Options step). */
  sprintID?: number;
  /**
   * S6b: also ensure the chosen sprint has a client packet. Ignored without a
   * `sprintID`. Idempotent per sprint — imports arrive in batches against one
   * sprint, so a sprint that already has a packet reuses it rather than
   * gaining a second.
   */
  createPacket?: boolean;
}

export interface CreativeImportConfirmResponse {
  creativeImportID: number;
  status: CreativeImportJobStatus;
  /**
   * S6b: the sprint's packet, when `createPacket` was requested — newly created
   * or the pre-existing one. Absent if not requested, or if packet creation
   * failed (deliberately non-fatal: the import is already queued by then).
   */
  sprintPacketID?: number;
}

export interface CreativeImportRowProgress {
  rowNumber: number;
  effectiveName: string;
  status: CreativeImportRowStatus;
  lastSuccessfulStep?: CreativeImportRowStep;
  /** First media child's created ids — the summary links the row, not each asset. */
  createdAdImageID?: number;
  createdAdVideoID?: number;
  createdAdCreativeID?: number;
  /** True when the created creative has since been deleted — render "(deleted)", not a link. */
  createdAdCreativeDeleted?: boolean;
  errorKind?: 'fb' | 'app';
  errorMessage?: string;
}

/** The business's currently running import, if any — powers the warn-but-allow concurrent-import banner. */
export interface CreativeImportActiveJob {
  creativeImportID: number;
  status: CreativeImportJobStatus;
}

/** One row of the "Recent imports" popover (slice 12). Business-wide, all statuses. */
export interface CreativeImportListItem {
  creativeImportID: number;
  status: CreativeImportJobStatus;
  createdAt: string;
  /** Display-only, derived from the staged S3 key — not a stored column. */
  archiveName?: string;
  /** ISO confirm deadline. Set only while status is awaiting_confirmation. */
  stagingExpiresAt?: string;
}

export interface CreativeImportProgress {
  creativeImportID: number;
  status: CreativeImportJobStatus;
  sprintID?: number;
  /** Job-level failure reason (row failures live on the rows). */
  errorMessage?: string;
  /** Selected rows only — unselected rows never process. */
  rows: CreativeImportRowProgress[];
  counts: {
    selected: number;
    done: number;
    error: number;
    remaining: number;
  };
}

// —— Per-row persisted state (S2 child-table shape; plan §6) ————————————————

export interface CreativeImportRowState {
  rowNumber: number;
  row: CreativeImportRow;
  effectiveName: string;
  /**
   * Persisted copy overrides (EC slice). Null/undefined means "no override —
   * fall back to the manifest value" in the worker; an empty string is an
   * explicit clear. Mirror the `effective*` seeds on the report.
   */
  effectivePrimaryText?: string | null;
  effectiveHeadline?: string | null;
  effectiveDescription?: string | null;
  effectiveCallToAction?: FBCallToAction | null;
  effectiveDestinationUrl?: string | null;
  effectiveJustification?: string | null;
  effectiveSlides?: CreativeImportSlideState[] | null;
  effectiveCopyPools?: CreativeImportCopyPools | null;
  hardErrors: CreativeImportIssue[];
  warnings: CreativeImportIssue[];
  /** Ordered upload state: one item per single asset, carousel slide, or dynamic asset. */
  media: Array<{
    ordinal: number;
    kind: CreativeImportMediaKind;
    stagedRef: string;
    createdAdImageID?: number | null;
    createdAdVideoID?: number | null;
  }>;
  selected: boolean;
  status: CreativeImportRowStatus;
  lastSuccessfulStep?: CreativeImportRowStep;
  createdAdImageID?: number;
  createdAdCreativeID?: number;
  /** Whether a processing failure came from Facebook or from our own app/system. */
  errorKind?: 'fb' | 'app';
  errorMessage?: string;
}

// —— Crash recovery (CR slice; plan §13.2) ——————————————————————————————————

/**
 * One row of the pre-confirm autosave snapshot. Unlike the confirm request,
 * this carries EVERY row with an explicit `selected` flag (so deselections
 * persist) and every copy field as a raw string (in-progress edits, not yet
 * cap-enforced — that stays a Confirm gate). Mirrors the frontend RowUIState.
 */
export interface CreativeImportAutosaveRow {
  rowNumber: number;
  selected: boolean;
  name: string;
  primaryText: string;
  headline: string;
  description: string;
  callToAction: string;
  destinationUrl: string;
  /** S7d. Raw in-progress edit; length is warn-only everywhere. */
  justification: string;
  /**
   * S7b/S7c. Present only for the format that has them. Autosave persists raw
   * values — the 255 caps stay a Confirm gate, matching the flat copy fields.
   */
  slides?: CreativeImportSlideState[];
  copyPools?: CreativeImportCopyPools;
}

/**
 * Full pre-confirm edit snapshot, autosaved to the awaiting_confirmation job so
 * a refresh/crash before Confirm doesn't lose the Review edits. `sprintID` is
 * nullable so clearing a previously-chosen sprint persists (the confirm
 * request's optional `sprintID` can't express "cleared").
 */
export interface CreativeImportAutosaveRequest {
  rows: CreativeImportAutosaveRow[];
  sprintID: number | null;
  /** S6b: the packet checkbox, preserved so reopening doesn't silently drop it. */
  createPacket: boolean;
}

/**
 * Rehydration payload for reopening an awaiting_confirmation import. The report
 * is reconstructed from the persisted rows (effective* reflect saved edits);
 * selection and sprint ride alongside so the frontend can restore the exact
 * pre-confirm Review state.
 */
export interface CreativeImportEditState {
  report: CreativeImportValidationReport;
  selectedRowNumbers: number[];
  sprintID: number | null;
  createPacket: boolean;
}
